import { useEffect, useMemo, useState } from 'react';

import { environment } from '../../../environments/environment';
import type { CompanyMapperSearchResult } from '../../../shared/types/company-result-item';
import type { WorkHistoryCompany } from './useWorkHistoryCompanies';

export type CompanyValidationStatus = 'pending' | 'valid' | 'invalid';

export interface ValidatedCompany {
  rcid: number;
  companyName: string;
  /** Revelio's canonical name from company mapper (may differ from profile name) */
  mappedName: string | null;
  status: CompanyValidationStatus;
  isCurrent: boolean;
  /** Whether this company's overlay is enabled on the chart */
  enabled: boolean;
}

/**
 * Validates work history companies against the company mapper API.
 * For each company, searches by name and checks if the RCID exists in the results.
 * Returns validated companies with their status (valid/invalid/pending).
 */
export const useValidateCompanies = (
  companies: WorkHistoryCompany[],
  currentCompanyRcid: number | null
): {
  validatedCompanies: ValidatedCompany[];
  isValidating: boolean;
} => {
  const [validationMap, setValidationMap] = useState<
    Map<number, { status: CompanyValidationStatus; mappedName: string | null }>
  >(new Map());
  const [isValidating, setIsValidating] = useState(false);

  /** Companies to validate: non-current ones with RCID > 0 */
  const companiesToValidate = useMemo(
    () => companies.filter((c) => !c.isCurrent && c.rcid > 0),
    [companies]
  );

  useEffect(() => {
    if (companiesToValidate.length === 0) {
      setValidationMap(new Map());
      return;
    }

    const controller = new AbortController();
    setIsValidating(true);

    const validate = async () => {
      const newMap = new Map<
        number,
        { status: CompanyValidationStatus; mappedName: string | null }
      >();

      // Validate all companies concurrently
      const results = await Promise.allSettled(
        companiesToValidate.map(async (company) => {
          try {
            const response = await fetch(
              `${environment.COMPANY_MAPPER_URL}/search`,
              {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                  name: company.companyName,
                  employee_cnt_min: 1,
                }),
                signal: controller.signal,
              }
            );

            if (!response.ok) {
              return {
                rcid: company.rcid,
                status: 'invalid' as const,
                mappedName: null,
              };
            }

            const searchResults: CompanyMapperSearchResult[] =
              await response.json();

            // Check if the exact RCID is in search results
            const match = searchResults.find((r) => r.rcid === company.rcid);
            if (match) {
              return {
                rcid: company.rcid,
                status: 'valid' as const,
                mappedName: match.primary_name,
              };
            }

            // RCID not found in search results — company is invalid
            return {
              rcid: company.rcid,
              status: 'invalid' as const,
              mappedName: null,
            };
          } catch (err: unknown) {
            if (err instanceof DOMException && err.name === 'AbortError') {
              throw err;
            }
            return {
              rcid: company.rcid,
              status: 'invalid' as const,
              mappedName: null,
            };
          }
        })
      );

      for (const result of results) {
        if (result.status === 'fulfilled') {
          newMap.set(result.value.rcid, {
            status: result.value.status,
            mappedName: result.value.mappedName,
          });
        }
      }

      if (!controller.signal.aborted) {
        setValidationMap(newMap);
        setIsValidating(false);
      }
    };

    validate();

    return () => controller.abort();
  }, [companiesToValidate]);

  const validatedCompanies = useMemo((): ValidatedCompany[] => {
    // Exclude current employer
    const pastCompanies = companies.filter((c) => !c.isCurrent);
    if (currentCompanyRcid) {
      return pastCompanies
        .filter((c) => c.rcid !== currentCompanyRcid)
        .map((company) => {
          const validation = validationMap.get(company.rcid);
          return {
            rcid: company.rcid,
            companyName: company.companyName,
            mappedName: validation?.mappedName ?? null,
            status: validation?.status ?? 'pending',
            isCurrent: false,
            enabled: false,
          };
        });
    }

    return pastCompanies.map((company) => {
      const validation = validationMap.get(company.rcid);
      return {
        rcid: company.rcid,
        companyName: company.companyName,
        mappedName: validation?.mappedName ?? null,
        status: validation?.status ?? 'pending',
        isCurrent: false,
        enabled: false,
      };
    });
  }, [companies, currentCompanyRcid, validationMap]);

  return { validatedCompanies, isValidating };
};
